/**
 * Pure helpers for the chat SSE stream lifecycle.
 *
 * Kept free of React/DOM imports so the connection logic can be unit tested in
 * isolation and reused by {@link ChatSocketProvider}.
 */
import { Message } from '@monetize/types/app';
import findLastIndex from 'lodash/findLastIndex';

export class ChatConnectionError extends Error {
  status: number;
  statusText: string;
  body: string;

  constructor(status: number, statusText: string, body: string) {
    super(`Chat connection failed: ${status} ${statusText}`);
    this.name = 'ChatConnectionError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export const friendlyConnectionMessage = (err: unknown): string => {
  if (err instanceof ChatConnectionError) {
    if (err.status === 401 || err.status === 403) {
      return "You don't have access to the AI assistant. Contact your administrator if you think this is a mistake.";
    }
    if (err.status === 429) {
      return 'The AI assistant is rate-limited right now. Please wait a moment and try again.';
    }
    if (err.status >= 500) {
      return 'The AI assistant is temporarily unavailable. Please try again in a moment.';
    }
    return `Couldn't reach the AI assistant (${err.status}). Please try again.`;
  }
  if (err instanceof TypeError) {
    return "Couldn't reach the AI assistant. Check your connection and try again.";
  }
  return 'Something went wrong connecting to the AI assistant. Please try again.';
};

export type StreamOutcome =
  | { kind: 'done' }
  | { kind: 'lost'; detail: string }
  | { kind: 'failed'; message: string; detail: string };

export const MAX_RECONNECT_ATTEMPTS = 3;

export const RECONNECT_BASE_DELAY_MS = 2000;

export const resolveStreamOutcome = ({
  receivedEnd,
  streamedContent,
  handledServerError = false,
  error,
}: {
  receivedEnd: boolean;
  streamedContent: boolean;
  handledServerError?: boolean;
  error?: unknown;
}): StreamOutcome => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  ) {
    return { kind: 'done' };
  }

  if (receivedEnd) {
    return { kind: 'done' };
  }

  if (handledServerError) {
    return { kind: 'done' };
  }

  if (error) {
    if (error instanceof ChatConnectionError) {
      if (error.status === 409) {
        // The backend's active_sessions guard (BP-18004) returns 409 when a
        // run is already in flight for this session -- a recoverable,
        // reconnect-style situation (another attempt for this same turn is
        // already active server-side), not a genuine unrecoverable failure.
        // Classifying it as 'lost' routes it through the same
        // check-status-before-resend recovery logic as a dropped
        // connection, instead of dead-ending on a raw error.
        return {
          kind: 'lost',
          detail: `${error.status} ${error.statusText} -- a run may already be active for this session`,
        };
      }
      return {
        kind: 'failed',
        message: friendlyConnectionMessage(error),
        detail: `${error.status} ${error.statusText}${
          error.body ? ` — ${error.body}` : ''
        }`,
      };
    }

    return {
      kind: 'lost',
      detail: error instanceof Error ? error.message : String(error),
    };
  }

  return {
    kind: 'lost',
    detail: streamedContent
      ? 'Connection closed before the end of the response'
      : 'Connection closed without a response',
  };
};

export const reconnectDelayMs = (
  attempt: number,
  random: () => number = Math.random,
): number => {
  const exponential = RECONNECT_BASE_DELAY_MS * 2 ** Math.max(0, attempt - 1);
  return Math.round(exponential + exponential * 0.25 * random());
};

export const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    signal?.addEventListener('abort', onAbort, { once: true });
  });

/**
 * A sub-agent's write-up streams long before the supervisor relays it (the
 * supervisor always rewrites the answer, typically near-verbatim), so the
 * live turn shows the write-up as the reply instead of making the user wait
 * for the second pass. Until the text is known to be a write-up it is held
 * back, because a sub-agent's narration ahead of a tool call ("The CP plan
 * search returned no results -- let me broaden that search.") streams the
 * same way and can run to several sentences; shown, it would flash in as a
 * reply (and collapse the work above it) before becoming an activity step.
 * Narration is a single paragraph, so text that breaks into paragraphs, or
 * whose step ended without a tool call, is the write-up.
 */
export const isDraftPreview = ({ content, isWriteUp }: Message) =>
  !!isWriteUp || content.includes('\n');

/**
 * What becomes of a turn's sub-agent messages once its stream ends, however
 * it ended. They stream hidden, with the latest one long enough to show
 * shown live as a draft until the supervisor's relay catches up with it (see
 * `chatTurns`), so settling keeps whatever the user was reading:
 * - no supervisor reply at all: the sub-agent text is the answer, or just
 *   the draft on screen if the stream was cut short;
 * - no supervisor reply after the latest draft: that draft is the answer;
 * - a relay after it: the drafts go, unless the stream was cut short (Stop,
 *   a dropped connection, a server error) before the relay caught up, in
 *   which case the relay keeps the draft's longer text rather than shrinking
 *   back to what it had re-typed so far. Only a relay that ran to the end is
 *   the final word, even if shorter.
 *
 * `turnReplies` are the assistant messages this stream created, oldest
 * first.
 */
export const settleSubAgentReplies = (
  messages: Message[],
  turnReplies: Message[],
  isComplete: boolean,
): Message[] => {
  const latestIndex = findLastIndex(
    turnReplies,
    ({ isSubAgentMessage }) => !!isSubAgentMessage,
  );
  if (latestIndex === -1) {
    return messages;
  }
  // Cut short, the draft on screen is the latest one known to be a write-up
  // (see `chatTurns`), not the start of a later step. A finished
  // stream's last word is its last message, however short.
  const shownIndex = isComplete
    ? -1
    : findLastIndex(
        turnReplies,
        (reply) => !!reply.isSubAgentMessage && isDraftPreview(reply),
      );
  const draftIndex = shownIndex === -1 ? latestIndex : shownIndex;
  const draft = turnReplies[draftIndex];
  const subAgentIds = new Set(
    turnReplies
      .filter(({ isSubAgentMessage }) => isSubAgentMessage)
      .map(({ id }) => id),
  );
  const promote = (ids: Set<string>) =>
    messages
      .filter(({ id }) => !subAgentIds.has(id) || ids.has(id))
      .map((message) =>
        ids.has(message.id)
          ? { ...message, isSubAgentMessage: false }
          : message,
      );

  if (turnReplies.every(({ isSubAgentMessage }) => isSubAgentMessage)) {
    // Cut short, the run may still finish with a supervisor reply, so keep
    // only the draft the user saw. Recovery restores that finished turn from
    // server history with its one reply; extra drafts here would make the
    // history look shorter than the local copy and get it rejected.
    return promote(isComplete ? subAgentIds : new Set([draft.id]));
  }
  const relays = turnReplies
    .slice(draftIndex + 1)
    .filter(({ isSubAgentMessage }) => !isSubAgentMessage);
  const relay = relays[relays.length - 1];
  if (!relay) {
    return promote(new Set([draft.id]));
  }
  const keepDraftText =
    !isComplete && relay.content.length < draft.content.length;
  return messages
    .filter(({ id }) => !subAgentIds.has(id))
    .map((message) =>
      keepDraftText && message.id === relay.id
        ? { ...message, content: draft.content }
        : message,
    );
};
